import { useRef, useState } from 'react';

/**
 * Encapsulates drag-and-drop file handling for any drop target element.
 *
 * Uses a counter ref to track nested drag enter/leave events fired by child
 * elements - without this, dragging over a child triggers a spurious leave on
 * the parent, causing `isDragging` to flicker false mid-drag.
 */
export function useDragAndDrop(onFileDrop: (files: File[]) => void | Promise<void>) {
  const [isDragging, setIsDragging] = useState(false);
  const dragCounterRef = useRef(0);

  function onDragEnter(e: React.DragEvent) {
    e.preventDefault();
    dragCounterRef.current += 1;
    setIsDragging(true);
  }

  function onDragOver(e: React.DragEvent) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  }

  function onDragLeave(e: React.DragEvent) {
    e.preventDefault();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  }

  async function onDrop(e: React.DragEvent) {
    e.preventDefault();
    dragCounterRef.current = 0;
    setIsDragging(false);
    await onFileDrop(Array.from(e.dataTransfer.files));
  }

  return { isDragging, onDragEnter, onDragOver, onDragLeave, onDrop };
}
